import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

const OrganizationContext = createContext(null);
const ACTIVE_ORGANIZATION_KEY = 'cybedefender.activeOrganizationGuid';

export function OrganizationProvider({ isAuthenticated, children }) {
  const [organizations, setOrganizations] = useState([]);
  const [activeOrganization, setActiveOrganization] = useState(null);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [organizationError, setOrganizationError] = useState('');

  const refreshOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setOrganizations([]);
      setActiveOrganization(null);
      setOrganizationError('');
      setOrganizationLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setOrganizations([]);
      setActiveOrganization(null);
      setOrganizationError('Authentication required.');
      setOrganizationLoading(false);
      return;
    }

    setOrganizationLoading(true);
    setOrganizationError('');

    try {
      const baseUrl = process.env.REACT_APP_CYBEDEFENDER_AI_URL || '/api';
      const response = await fetch(`${baseUrl}/cybedefender/organizations`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Organizations request failed: ${response.status}`);
      }

      const data = await response.json();
      const authorizedOrganizations = Array.isArray(data.organizations)
        ? data.organizations
        : [];

      const storedGuid = localStorage.getItem(ACTIVE_ORGANIZATION_KEY);
      const selected =
        authorizedOrganizations.find((item) => item.guid === storedGuid) ||
        authorizedOrganizations[0] ||
        null;

      setOrganizations(authorizedOrganizations);
      setActiveOrganization(selected);

      if (selected) {
        localStorage.setItem(ACTIVE_ORGANIZATION_KEY, selected.guid);
      } else {
        localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
      }
    } catch (error) {
      setOrganizations([]);
      setActiveOrganization(null);
      setOrganizationError(error.message || 'Failed to load organizations.');
    } finally {
      setOrganizationLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
    }
    refreshOrganizations();
  }, [isAuthenticated, refreshOrganizations]);

  const selectOrganization = useCallback(
    (organizationGuid) => {
      const selected = organizations.find((item) => item.guid === organizationGuid);
      if (!selected) return false;

      setActiveOrganization(selected);
      localStorage.setItem(ACTIVE_ORGANIZATION_KEY, selected.guid);
      return true;
    },
    [organizations]
  );

  const value = useMemo(
    () => ({
      organizations,
      activeOrganization,
      organizationLoading,
      organizationError,
      selectOrganization,
      refreshOrganizations
    }),
    [
      organizations,
      activeOrganization,
      organizationLoading,
      organizationError,
      selectOrganization,
      refreshOrganizations
    ]
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used inside OrganizationProvider');
  }
  return context;
}

export { ACTIVE_ORGANIZATION_KEY };
